import { Link } from "react-router-dom";
import { Clock, MapPin, Users, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { StatusChip } from "./DestinationCard";
import { useLead } from "../lib/store";
import { track } from "../lib/track";
import { WhatsAppIcon } from "./icons";

export default function PackageCard({ pkg, index = 0 }) {
  const { openLead } = useLead();

  return (
    <Reveal delay={Math.min(index * 0.07, 0.3)} className="h-full">
      <div
        data-testid={`package-card-${pkg.slug}`}
        className="flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-crystal card-hover"
      >
        <div className="relative h-48 overflow-hidden sm:h-52">
          <img
            src={pkg.hero_image}
            alt={pkg.title}
            loading={index > 2 ? "lazy" : "eager"}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <StatusChip status={pkg.status} testid={`package-status-${pkg.slug}`} />
            <span className="chip bg-white/90 text-charcoal">
              <Clock className="h-3.5 w-3.5 text-tosca-600" /> {pkg.duration}
            </span>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-tosca-600">
            <MapPin className="h-3.5 w-3.5" /> {pkg.destination_name} · {pkg.trip_type}
          </p>
          <h3 className="mt-2 font-heading text-xl font-bold leading-snug text-charcoal">{pkg.title}</h3>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {pkg.services_included.slice(0, 4).map((s) => (
              <span key={s} className="chip bg-tosca-50 text-tosca-700">{s}</span>
            ))}
            {pkg.services_included.length > 4 && (
              <span className="chip bg-slate-100 text-slate-500">+{pkg.services_included.length - 4} lainnya</span>
            )}
          </div>

          <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-charcoal">
            <Users className="h-4 w-4 text-gold" /> Starting From
            <span className="text-gold-dark">Request Quotation</span>
          </p>

          <div className="mt-5 flex gap-2 pt-2">
            <Link
              to={`/land-arrangement/${pkg.slug}`}
              data-testid={`package-view-details-${pkg.slug}`}
              className="btn btn-primary flex-1 !px-4 !text-[13px]"
            >
              View Details <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => {
                track("request_quotation", { package: pkg.slug, destination: pkg.destination_slug });
                openLead({ destination: pkg.destination_name, source: `package:${pkg.slug}` });
              }}
              data-testid={`package-request-quotation-${pkg.slug}`}
              className="btn btn-outline !px-4 !text-[13px]"
              aria-label="Request Quotation"
            >
              <WhatsAppIcon className="h-4 w-4" /> Quotation
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
